import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../../Global/Layout';
import { showConfirm, showSuccess, showError } from '../../../utils/sweetAlertConfig';
import { API_CONFIG } from '../../../contexts/constants';
import Select from 'react-select';

const Config = ({ onLogout, userRole }) => {
	const [config, setConfig] = useState({
		id: null,
    	promoMessage: '',
		disableAgentLogin: false,
		disableCustomerLogin: false,
		disableSystem: false,
		quantity: 10,
		quantityNew: 1,
		bonus: 1,
		bonusNew: 1,
		quantityMegaNew: 1,
		bonusMegaNew: 1,
		quantityMega: 10,
		bonusMega: 1,
		lineLink: '',
		videoLink: '',
		payBankId: null,
		payBankName: '',
		payBankOtherName: '',
		payBankAccountName: '',
		payBankAccountNumber: '',
		payPromptPay: '',
		payPromptPayName: ''
	});
	const [loading, setLoading] = useState(true);
	const [bankOptions, setBankOptions] = useState([]);

	// ดึงข้อมูล config จาก API
	useEffect(() => {
		(async () => {
			const options = await fetchBanks();
			await fetchConfig(options);
		})();
	}, []);

	const fetchBanks = async () => {
		try {
			const token = localStorage.getItem('token');
			const response = await axios.get(`${API_CONFIG.BASE_URL}/banks`, {
				headers: { Authorization: `Bearer ${token}` }
			});

			if (response.data.success && Array.isArray(response.data.data)) {
				const options = response.data.data.map(b => ({ value: b.id, label: b.name }));
				// ensure id 99 (Other) exists as an option
				if (!options.find(o => Number(o.value) === 99)) {
					options.push({ value: 99, label: 'อื่นๆ (ระบุ)' });
				}
				setBankOptions(options);
				return options;
			}
			return [];
		} catch (error) {
			console.error('Error fetching banks:', error);
			return [];
		}
	};

	if (!['sysadmin', 'owner'].includes(userRole)) {
		window.location.href = '/forbidden';
		return null;
	}

	const fetchConfig = async (banks = []) => {
		try {
			const token = localStorage.getItem('token');
			const response = await axios.get(`${API_CONFIG.BASE_URL}/config`, {
				headers: {
					Authorization: `Bearer ${token}`
				}
			});

			if (response.data.success) {
				const data = response.data.data;
				const lookup = (banks && banks.length) ? banks : bankOptions;
				let payBankId = data.pay_bank_id || null;

				// if no payBankId but has pay_bank_name, try to map it
				if (!payBankId && data.pay_bank_name) {
					const match = lookup.find(b => b.label === data.pay_bank_name);
					if (match) payBankId = match.value;
					else payBankId = 99; // other
				}

				setConfig({
					id: data.id,
					promoMessage: data.promotion_news || '',
					disableAgentLogin: !data.login_status_agent,
					disableCustomerLogin: !data.login_status_customer,
					disableSystem: !data.system_status,
					quantity: data.customer_pb_old_count || 10,
					quantityNew: data.customer_pb_new_count || 1,
					bonus: data.customer_pb_old_free || 1,
					bonusNew: data.customer_pb_new_free || 1,
					quantityMegaNew: data.customer_mg_new_count || 1,
					bonusMegaNew: data.customer_mg_new_free || 1,
					quantityMega: data.customer_mg_old_count || 10,
					bonusMega: data.customer_mg_old_free || 1,
					lineLink: data.line_account || '',
					videoLink: data.youtube_login || '',
					payBankId: payBankId,
					payBankName: payBankId || '',
					payBankOtherName: Number(payBankId) === 99 ? (data.pay_bank_name || '') : '',
					payBankAccountName: data.pay_bank_account_name || '',
					payBankAccountNumber: data.pay_bank_account || '',
					payPromptPay: data.pay_prompt_pay || '',
					payPromptPayName: data.pay_prompt_pay_name || ''
				});
			}
		} catch (error) {
			console.error('Error fetching config:', error);
			showError('ผิดพลาด!', 'ไม่สามารถโหลดข้อมูลการตั้งค่าได้');
		} finally {
			setLoading(false);
		}
	};

	const handleCheckboxChange = (field) => {
		setConfig(prev => ({
			...prev,
			[field]: !prev[field]
		}));
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setConfig(prev => ({
			...prev,
			[name]: value
		}));
	};

	const handleBankChange = (selectedOption) => {
		setConfig(prev => ({
			...prev,
			payBankName: selectedOption ? selectedOption.value : '',
			payBankOtherName: selectedOption && Number(selectedOption.value) === 99 ? (prev.payBankOtherName || '') : ''
		}));
	};

	const handleSave = async () => {
		const result = await showConfirm({
			title: 'บันทึกการตั้งค่า?',
			text: 'คุณต้องการบันทึกการตั้งค่าระบบหรือไม่?',
			confirmText: 'บันทึก',
			cancelText: 'ยกเลิก',
			reverseButtons: true
		});

		if (result.isConfirmed) {
			try {
				const token = localStorage.getItem('token');
				const userId = localStorage.getItem('userID');

				// ตรวจสอบว่ามี config id หรือไม่
				if (!config.id) {
					showError('ผิดพลาด!', 'ไม่พบข้อมูลการตั้งค่า กรุณาโหลดหน้าใหม่');
					return;
				}

				// Validate bank account number - digits only
				if (config.payBankAccountNumber && !/^\d+$/.test(config.payBankAccountNumber)) {
					showError('ผิดพลาด!', 'เลขที่บัญชีต้องเป็นตัวเลขเท่านั้น');
					return;
				}

				// Validate PromptPay - digits only
				if (config.payPromptPay && !/^\d+$/.test(config.payPromptPay)) {
					showError('ผิดพลาด!', 'พร้อมเพย์ต้องเป็นตัวเลขเท่านั้น');
					return;
				}

				// Validate bank name when bank_id=99 (Other)
				if (Number(config.payBankName) === 99 && !config.payBankOtherName) {
					showError('ผิดพลาด!', 'กรุณาระบุชื่อธนาคาร');
					return;
				}

				const payload = {
					promotion_news: config.promoMessage || '',
					customer_pb_new_count: parseInt(config.quantityNew) || 0,
					customer_pb_new_free: parseInt(config.bonusNew) || 0,
					customer_pb_old_count: parseInt(config.quantity) || 0,
					customer_pb_old_free: parseInt(config.bonus) || 0,
					customer_mg_new_count: parseInt(config.quantityMegaNew) || 0,
					customer_mg_new_free: parseInt(config.bonusMegaNew) || 0,
					customer_mg_old_count: parseInt(config.quantityMega) || 0,
					customer_mg_old_free: parseInt(config.bonusMega) || 0,
					line_account: config.lineLink || '',
					youtube_login: config.videoLink || '',
					login_status_agent: !config.disableAgentLogin ? 1 : 0,
					login_status_customer: !config.disableCustomerLogin ? 1 : 0,
					system_status: !config.disableSystem ? 1 : 0,
					pay_bank_id: config.payBankName || null,
					pay_bank_name: Number(config.payBankName) === 99 ? (config.payBankOtherName || '') : (bankOptions.find(b => b.value === config.payBankName)?.label || ''),
					pay_bank_account: config.payBankAccountNumber || '',
					pay_bank_account_name: config.payBankAccountName || '',
					pay_prompt_pay: config.payPromptPay || '',
					pay_prompt_pay_name: config.payPromptPayName || '',
					updated_user: userId
				};

				// console.log('Sending payload to API:', payload);

				const response = await axios.put(
					`${API_CONFIG.BASE_URL}/config`,
					payload,
					{
						headers: {
							Authorization: `Bearer ${token}`,
							'Content-Type': 'application/json'
						}
					}
				);

				if (response.data.success) {
					await fetchConfig();
					await showSuccess('สำเร็จ!', 'บันทึกการตั้งค่าเรียบร้อยแล้ว');
				} else {
					console.log('API Response:', response.data);
					showError('ผิดพลาด!', response.data.message || 'ไม่สามารถบันทึกการตั้งค่าได้');
				}
			} catch (error) {
				console.error('Error saving config:', error);
				const errorMessage = error.response?.data?.message || error.message || 'ไม่สามารถบันทึกการตั้งค่าได้';
				showError('ผิดพลาด!', errorMessage);
			}
		}
	};

	if (loading) {
		return (
			<Layout title="ตั้งค่าระบบ" onLogout={onLogout} userRole={userRole}>
				<div className="flex justify-center items-center h-64">
					<div className="text-gray-500 dark:text-gray-400">กำลังโหลดข้อมูล...</div>
				</div>
			</Layout>
		);
	}

	return (
		<Layout title="ตั้งค่าระบบ" onLogout={onLogout} userRole={userRole}>
			<div className="space-y-6">
				<div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
					<h2 className="text-xl font-semibold mb-6 text-gray-800 dark:text-white">
						การตั้งค่าข้อความข่าวสาร/โปรโมชั่นหน้าแรก
					</h2>

          <div className="grid grid-cols-1 gap-6 mb-7">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                ข้อความ
              </label>
              <textarea
                name="promoMessage"
                value={config.promoMessage}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="ระบุข้อความข่าวสาร/โปรโมชั่น"
                rows={4}
              />
            </div>
          </div>
				</div>

				<div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
					<h2 className="text-xl font-semibold mb-3 text-gray-800 dark:text-white">
						การตั้งค่าโปรโมชั่น Powerball
					</h2>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">** หากไม่ต้องการให้มีโปรโมชั่นให้ใส่เป็น 0 หรือ ว่าง</p>
          <div className="mb-7">
            <h3 className="flex item-start gap-2 text-lg font-semibold mb-3 text-gray-800 dark:text-white">
              <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                <path d="M144 128a80 80 0 1 1 160 0 80 80 0 1 1 -160 0zm208 0a128 128 0 1 0 -256 0 128 128 0 1 0 256 0zM48 480c0-70.7 57.3-128 128-128l96 0c70.7 0 128 57.3 128 128l0 8c0 13.3 10.7 24 24 24s24-10.7 24-24l0-8c0-97.2-78.8-176-176-176l-96 0C78.8 304 0 382.8 0 480l0 8c0 13.3 10.7 24 24 24s24-10.7 24-24l0-8z"/>
              </svg>
              ลูกค้าใหม่
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  จำนวนที่ซื้อ
                </label>
                <input
                  type="number"
                  name="quantityNew"
                  value={config.quantityNew}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="ระบุจำนวนที่ซื้อ"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  จำนวนที่แถม
                </label>
                <input
                  type="number"
                  name="bonusNew"
                  value={config.bonusNew}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="ระบุจำนวนที่แถม"
                />
              </div>
            </div>
          </div>
          <hr/>
          <div className="mt-6 mb-7">
            <h3 className="flex item-start gap-2 text-lg font-semibold mb-3 text-gray-800 dark:text-white">
              <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                <path d="M144 128a80 80 0 1 1 160 0 80 80 0 1 1 -160 0zm208 0a128 128 0 1 0 -256 0 128 128 0 1 0 256 0zM48 480c0-70.7 57.3-128 128-128l96 0c70.7 0 128 57.3 128 128l0 8c0 13.3 10.7 24 24 24s24-10.7 24-24l0-8c0-97.2-78.8-176-176-176l-96 0C78.8 304 0 382.8 0 480l0 8c0 13.3 10.7 24 24 24s24-10.7 24-24l0-8z"/>
              </svg>
              ลูกค้าปัจจุบัน
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  จำนวนที่ซื้อ
                </label>
                <input
                  type="number"
                  name="quantity"
                  value={config.quantity}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="ระบุจำนวนที่ซื้อ"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  จำนวนที่แถม
                </label>
                <input
                  type="number"
                  name="bonus"
                  value={config.bonus}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="ระบุจำนวนที่แถม"
                />
              </div>
            </div>
          </div>
				</div>

				<div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
					<h2 className="text-xl font-semibold mb-3 text-gray-800 dark:text-white">
						การตั้งค่าโปรโมชั่น Mega Millions
					</h2>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">** หากไม่ต้องการให้มีโปรโมชั่นให้ใส่เป็น 0 หรือ ว่าง</p>
          <div className="mb-7">
            <h3 className="flex item-start gap-2 text-lg font-semibold mb-3 text-gray-800 dark:text-white">
              <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                <path d="M144 128a80 80 0 1 1 160 0 80 80 0 1 1 -160 0zm208 0a128 128 0 1 0 -256 0 128 128 0 1 0 256 0zM48 480c0-70.7 57.3-128 128-128l96 0c70.7 0 128 57.3 128 128l0 8c0 13.3 10.7 24 24 24s24-10.7 24-24l0-8c0-97.2-78.8-176-176-176l-96 0C78.8 304 0 382.8 0 480l0 8c0 13.3 10.7 24 24 24s24-10.7 24-24l0-8z"/>
              </svg>
              ลูกค้าใหม่
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  จำนวนที่ซื้อ
                </label>
                <input
                  type="number"
                  name="quantityMegaNew"
                  value={config.quantityMegaNew}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="ระบุจำนวนที่ซื้อ"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  จำนวนที่แถม
                </label>
                <input
                  type="number"
                  name="bonusMegaNew"
                  value={config.bonusMegaNew}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="ระบุจำนวนที่แถม"
                />
              </div>
            </div>
          </div>
          <hr/>
          <div className="mt-6 mb-7">
            <h3 className="flex item-start gap-2 text-lg font-semibold mb-3 text-gray-800 dark:text-white">
              <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                <path d="M144 128a80 80 0 1 1 160 0 80 80 0 1 1 -160 0zm208 0a128 128 0 1 0 -256 0 128 128 0 1 0 256 0zM48 480c0-70.7 57.3-128 128-128l96 0c70.7 0 128 57.3 128 128l0 8c0 13.3 10.7 24 24 24s24-10.7 24-24l0-8c0-97.2-78.8-176-176-176l-96 0C78.8 304 0 382.8 0 480l0 8c0 13.3 10.7 24 24 24s24-10.7 24-24l0-8z"/>
              </svg>
              ลูกค้าปัจจุบัน
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  จำนวนที่ซื้อ
                </label>
                <input
                  type="number"
                  name="quantityMega"
                  value={config.quantityMega}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="ระบุจำนวนที่ซื้อ"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  จำนวนที่แถม
                </label>
                <input
                  type="number"
                  name="bonusMega"
                  value={config.bonusMega}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="ระบุจำนวนที่แถม"
                />
              </div>
            </div>
          </div>
				</div>

				<div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
					<h2 className="text-xl font-semibold mb-3 text-gray-800 dark:text-white">
						การตั้งค่าบัญชีสำหรับจ่ายเงิน
					</h2>
					<p className="text-gray-500 dark:text-gray-400 text-sm mb-4">** กรอกข้อมูลบัญชีธนาคารหรือ PromptPay ที่ใช้สำหรับจ่ายเงินให้ลูกค้า</p>
					<div className="space-y-4">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{/* Bank Name */}
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
									ธนาคาร
								</label>
								<Select
									options={bankOptions}
									value={bankOptions.find(option => option.value === config.payBankName)}
									onChange={handleBankChange}
									isClearable
									placeholder="เลือกธนาคาร"
									className="react-select-container"
									classNamePrefix="react-select"
									styles={{
										control: (base) => ({
											...base,
											backgroundColor: 'transparent',
											borderColor: '#d1d5db',
											'&:hover': { borderColor: '#9ca3af' }
										}),
										menu: (base) => ({
											...base,
											backgroundColor: document.documentElement.classList.contains('dark') ? '#374151' : '#ffffff',
											zIndex: 50
										}),
										option: (base, state) => ({
											...base,
											backgroundColor: state.isFocused
												? (document.documentElement.classList.contains('dark') ? '#4b5563' : '#f3f4f6')
												: 'transparent',
											color: document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#111827',
											'&:active': {
												backgroundColor: document.documentElement.classList.contains('dark') ? '#4b5563' : '#e5e7eb'
											}
										}),
										singleValue: (base) => ({
											...base,
											color: document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#111827'
										}),
										input: (base) => ({
											...base,
											color: document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#111827'
										})
									}}
								/>
							</div>

							{/* Bank Other Name (when selected Other) */}
							{Number(config.payBankName) === 99 && (
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
										ระบุชื่อธนาคาร
									</label>
									<input
										type="text"
										name="payBankOtherName"
										value={config.payBankOtherName || ''}
										onChange={handleInputChange}
										className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
										placeholder="กรอกชื่อธนาคาร"
									/>
								</div>
							)}
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{/* Bank Account Name */}
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
									ชื่อบัญชี
								</label>
								<input
									type="text"
									name="payBankAccountName"
									value={config.payBankAccountName}
									onChange={handleInputChange}
									className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
									placeholder="ชื่อบัญชีธนาคาร"
								/>
							</div>

							{/* Bank Account Number */}
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
									เลขที่บัญชี
								</label>
								<input
									type="text"
									name="payBankAccountNumber"
									value={config.payBankAccountNumber}
									onChange={handleInputChange}
									onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
									className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
									placeholder="เลขที่บัญชีธนาคาร"
								/>
							</div>

							{/* PromptPay Name */}
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
									ชื่อบัญชีพร้อมเพย์
								</label>
								<input
									type="text"
									name="payPromptPayName"
									value={config.payPromptPayName}
									onChange={handleInputChange}
									className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
									placeholder="ชื่อบัญชีพร้อมเพย์"
								/>
							</div>

							{/* PromptPay */}
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
									เลขพร้อมเพย์
								</label>
								<input
									type="text"
									name="payPromptPay"
									value={config.payPromptPay}
									onChange={handleInputChange}
									onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
									className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
									placeholder="เลขบัตรประชาชน / เบอร์โทรศัพท์"
								/>
							</div>					</div>
					</div>
				</div>

				<div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
					<h2 className="text-xl font-semibold mb-6 text-gray-800 dark:text-white">
						การตั้งค่าไลน์ (Line)
					</h2>
					<div className="space-y-4">
            <div>
              <input
                type="text"
                name="lineLink"
                value={config.lineLink}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="ex. https://line.me/ti/p/@yourlineid"
              />
            </div>
					</div>
				</div>

				<div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
					<h2 className="text-xl font-semibold mb-6 text-gray-800 dark:text-white">
						การตั้งค่าวิดีโอ หน้าเข้าสู่ระบบ
					</h2>
					<div className="space-y-4">
						<div>
							<input
								type="text"
								name="videoLink"
								value={config.videoLink}
								onChange={handleInputChange}
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
								placeholder="ex. https://www.youtube.com/watch?v=8ssV-zBJ4U0"
							/>
						</div>
					</div>
				</div>

				<div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
					<h2 className="text-xl font-semibold mb-6 text-gray-800 dark:text-white">
						การตั้งค่าระบบ
					</h2>

					<div className="space-y-4">
						{/* ปิดการเข้าสู่ระบบของตัวแทน */}
						<div className="flex items-center">
							<input
								type="checkbox"
								id="disableAgentLogin"
								checked={config.disableAgentLogin}
								onChange={() => handleCheckboxChange('disableAgentLogin')}
								className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 cursor-pointer"
							/>
							<label
								htmlFor="disableAgentLogin"
								className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300 cursor-pointer"
							>
								ปิดการเข้าสู่ระบบของตัวแทน
							</label>
						</div>

						{/* ปิดการเข้าสู่ระบบของลูกค้า */}
						<div className="flex items-center">
							<input
								type="checkbox"
								id="disableCustomerLogin"
								checked={config.disableCustomerLogin}
								onChange={() => handleCheckboxChange('disableCustomerLogin')}
								className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 cursor-pointer"
							/>
							<label
								htmlFor="disableCustomerLogin"
								className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300 cursor-pointer"
							>
								ปิดการเข้าสู่ระบบของลูกค้า
							</label>
						</div>

						{/* ปิดระบบ */}
						<div className="flex items-center">
							<input
								type="checkbox"
								id="disableSystem"
								checked={config.disableSystem}
								onChange={() => handleCheckboxChange('disableSystem')}
								className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 cursor-pointer"
							/>
							<label
								htmlFor="disableSystem"
								className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300 cursor-pointer"
							>
								ปิดระบบ
							</label>
						</div>
					</div>
				</div>

        {/* ปุ่มบันทึก */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors"
          >
            บันทึกการตั้งค่า
          </button>
        </div>
			</div>
		</Layout>
	);
};

export default Config;
